// Activity — who did what, when. A full-screen view of its own (own look,
// own tokens in activity.css), opened from the admin console by super
// admins. Reads the activity log of the instance the console is pointed at
// (useConsole().client), so another instance's activity — and its client
// data — is read on that instance, never copied here.
//
// Views: Overview (figures, trend, when people work, what gets created,
// what failed) · People (everyone, drill-down to a person's timeline and
// conversations) · Conversations (inbox + full transcript) · Live feed.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faGaugeHigh, faUsers, faComments, faTowerBroadcast, faArrowsRotate, faArrowLeft, faMagnifyingGlass,
  faTriangleExclamation, faMicrophone, faClock, faHeadphones, faMagnifyingGlassChart, faFileLines, faUserClock,
  faChevronRight, faShieldHalved, faServer, faDatabase, faWaveSquare,
} from '@fortawesome/free-solid-svg-icons';
import { useConsole } from '../admin/context';
import { Avatar, EventDetail, Transcript } from './Detail';
import { BarList, DayStrip, Heatmap, Spark, StackedBars } from './charts';
import {
  CATEGORIES, CATEGORY, PERIODS, ago, areaLabel, compact, dayKey, dayLabel, delta, describe, docLabel, docTitle, duration,
  fullName, num, seriesByBucket, timeOf,
} from './format';
import './activity.css';

const VIEWS = [
  { key: 'overview', label: 'Overview', icon: faGaugeHigh },
  { key: 'people', label: 'People', icon: faUsers },
  { key: 'conversations', label: 'Conversations', icon: faComments },
  { key: 'feed', label: 'Live feed', icon: faTowerBroadcast },
];

const store = {
  get: (k, d) => { try { return JSON.parse(localStorage.getItem(`activity:${k}`)) ?? d; } catch (e) { return d; } },
  set: (k, v) => { try { localStorage.setItem(`activity:${k}`, JSON.stringify(v)); } catch (e) { /* ignore */ } },
};

function useTicker(ms) {
  const [, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((t) => t + 1), ms); return () => clearInterval(id); }, [ms]);
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ActivityCenter({ onClose }) {
  const { client, meta } = useConsole();
  const [view, setView] = useState({ name: store.get('view', 'overview') });
  const [hours, setHoursState] = useState(store.get('hours', 168));
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [loadedAt, setLoadedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState(null);           // { thread } | { event }
  const [rev, setRev] = useState(0);                    // bumps child reloads
  useTicker(15000);

  const setHours = (h) => { setHoursState(h); store.set('hours', h); };
  const go = useCallback((name, extra = {}) => {
    setView({ name, ...extra });
    if (name !== 'person') store.set('view', name);
    setDrawer(null);
  }, []);

  const load = useCallback(async (quiet) => {
    if (!quiet) setBusy(true);
    try {
      setOverview(await client.get(`/api/admin/activity/overview?hours=${hours}`));
      setError('');
      setLoadedAt(Date.now());
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }, [client, hours]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load(true); }, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (drawer) setDrawer(null); else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [drawer, onClose]);

  const refresh = () => { load(); setRev((r) => r + 1); };
  const openPerson = (id) => { if (id) go('person', { id }); };
  const openThread = (key) => setDrawer({ thread: key });
  const openEvent = (e) => (e.kind === 'chat' && e.thread ? setDrawer({ thread: e.thread }) : setDrawer({ event: e.id }));

  const people = overview?.people || [];
  const period = PERIODS.find((p) => p.hours === hours) || PERIODS[1];
  const instance = overview?.instance || meta?.instance?.instance_label || 'this instance';
  const title = view.name === 'person' ? 'Person' : VIEWS.find((v) => v.key === view.name)?.label;
  const shared = { client, hours, period, people, rev, onPerson: openPerson, onThread: openThread, onEvent: openEvent };

  return (
    <div className="ac-root" role="dialog" aria-modal="true" aria-label="Activity">
      <aside className="ac-side-nav">
        <div className="ac-brand">
          <span className="ac-brand-mark"><FontAwesomeIcon icon={faWaveSquare} /></span>
          <div>
            <div className="ac-brand-name">Activity</div>
            <div className="ac-brand-sub"><FontAwesomeIcon icon={faServer} /> {instance}</div>
          </div>
        </div>
        <nav className="ac-nav">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" onClick={() => go(v.key)}
              className={view.name === v.key || (view.name === 'person' && v.key === 'people') ? 'active' : ''}>
              <FontAwesomeIcon icon={v.icon} fixedWidth /> <span>{v.label}</span>
              {v.key === 'people' && <em>{people.length || ''}</em>}
              {v.key === 'feed' && <i className="ac-live-dot" />}
            </button>
          ))}
        </nav>
        <div className="ac-side-foot">
          <Retention client={client} days={overview?.retention_days} onSaved={() => load(true)} />
          <div className="ac-privacy"><FontAwesomeIcon icon={faShieldHalved} /> Super admins only. Stays on {instance}.</div>
        </div>
      </aside>

      <main className="ac-main">
        <header className="ac-top">
          <div className="ac-top-title">
            <h1>{title}</h1>
            <p>{period.long} · {loadedAt ? `updated ${ago(new Date(loadedAt).toISOString())}` : 'loading…'}</p>
          </div>
          <div className="ac-top-actions">
            <div className="ac-seg" role="tablist" aria-label="Period">
              {PERIODS.map((p) => (
                <button key={p.hours} type="button" className={p.hours === hours ? 'on' : ''} onClick={() => setHours(p.hours)}>{p.label}</button>
              ))}
            </div>
            <button type="button" className="ac-icon-btn" onClick={refresh} title="Refresh" disabled={busy}>
              <FontAwesomeIcon icon={faArrowsRotate} spin={busy} />
            </button>
            <button type="button" className="ac-icon-btn" onClick={onClose} title="Close (Esc)"><FontAwesomeIcon icon={faXmark} /></button>
          </div>
        </header>

        <div className="ac-scroll">
          {error && <div className="ac-alert big"><FontAwesomeIcon icon={faTriangleExclamation} /> {error}</div>}
          {!overview && !error && <div className="ac-empty tall"><span className="ac-spinner" /></div>}
          {overview && view.name === 'overview' && <Overview data={overview} go={go} {...shared} />}
          {overview && view.name === 'people' && <People {...shared} />}
          {overview && view.name === 'person' && <Person id={view.id} back={() => go('people')} {...shared} />}
          {overview && view.name === 'conversations' && <Conversations {...shared} />}
          {overview && view.name === 'feed' && <Feed {...shared} />}
        </div>
      </main>

      {drawer && (
        <div className="ac-drawer-wrap" onClick={() => setDrawer(null)}>
          <section className="ac-drawer" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="ac-icon-btn ac-drawer-close" onClick={() => setDrawer(null)} title="Close"><FontAwesomeIcon icon={faXmark} /></button>
            {drawer.thread && <Transcript client={client} threadKey={drawer.thread} onPerson={openPerson} />}
            {drawer.event && <EventDetail client={client} eventId={drawer.event} onPerson={openPerson} onThread={openThread} />}
          </section>
        </div>
      )}
    </div>
  );
}

function Retention({ client, days, onSaved }) {
  const [editing, setEditing] = useState(false);
  const save = async (v) => {
    try { await client.put('/api/admin/activity/settings', { retention_days: Number(v) }); onSaved(); } catch (e) { window.alert(e.message); }
    setEditing(false);
  };
  if (!days) return null;
  return (
    <div className="ac-retention">
      <FontAwesomeIcon icon={faUserClock} />
      {editing ? (
        <select autoFocus defaultValue={days} onChange={(e) => save(e.target.value)} onBlur={() => setEditing(false)}>
          {[30, 90, 180, 365, 730].map((d) => <option key={d} value={d}>{d} days</option>)}
        </select>
      ) : (
        <button type="button" onClick={() => setEditing(true)} title="Change how long activity is kept">Kept {days} days</button>
      )}
    </div>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────

function Card({ title, icon, action, children, className = '' }) {
  return (
    <section className={`ac-card ${className}`}>
      {(title || action) && (
        <header className="ac-card-head">
          <h3>{icon && <FontAwesomeIcon icon={icon} />} {title}</h3>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

function Kpi({ label, value, sub, d, tone, spark, sparkColor, alert, badge }) {
  return (
    <div className={`ac-kpi${alert ? ' alert' : ''}`}>
      <div className="ac-kpi-top">
        <span className="ac-kpi-label">{label}</span>
        {d && <span className={`ac-delta ${d.tone}`}>{d.text}</span>}
        {badge && <span className="ac-delta down">{badge}</span>}
      </div>
      <div className={`ac-kpi-value${tone ? ` ac-text-${tone}` : ''}`}>{value}</div>
      <div className="ac-kpi-sub">{sub}</div>
      {spark && <Spark values={spark} color={sparkColor} />}
    </div>
  );
}

function EventRow({ e, onEvent, onPerson, showUser = true, compactRow = false }) {
  const s = describe(e);
  const cat = CATEGORY[e.category] || CATEGORY.access;
  const failed = e.status === 'error' || e.status === 'aborted';
  return (
    <div className={`ac-ev${failed ? ' error' : ''}${compactRow ? ' compact' : ''}`}>
      <span className={`ac-icon ac-cat-${e.category}`}><FontAwesomeIcon icon={failed ? faTriangleExclamation : cat.icon} /></span>
      <button type="button" className="ac-ev-body" onClick={() => onEvent(e)}>
        <div className="ac-ev-line">
          {showUser && <b className="ac-ev-user" onClick={(x) => { x.stopPropagation(); onPerson(e.user_id); }}>{e.username || 'unknown'}</b>}
          <span className="ac-ev-verb">{s.verb}</span>
          {e.channel === 'voice' && <FontAwesomeIcon icon={faMicrophone} className="ac-muted" />}
        </div>
        {s.object && <div className="ac-ev-object">{s.object}</div>}
        {!compactRow && e.kind === 'chat' && e.answer_preview && (
          <div className="ac-ev-preview">{e.answer_preview.replace(/[#*_|`>-]+/g, ' ').replace(/\s+/g, ' ').trim()}</div>
        )}
        {compactRow && e.status === 'aborted' && <div className="ac-ev-object ac-text-error">broken off before the answer was complete</div>}
        {!compactRow && (e.kind === 'chat' || e.outputs?.length > 0 || e.error || e.status === 'aborted') && (
          <div className="ac-ev-chips">
            {e.kind === 'chat' && <span><FontAwesomeIcon icon={faClock} /> {duration(e.duration_ms)}</span>}
            {e.tool_count > 0 && <span>{e.tool_count} tool calls</span>}
            {e.data_set && <span><FontAwesomeIcon icon={faDatabase} /> {e.data_set}</span>}
            {(e.outputs || []).map((o, i) => <span key={i} className="ac-chip-doc">{docLabel(o.type)}</span>)}
            {e.error && <span className="ac-chip-err">{e.error.slice(0, 90)}</span>}
            {e.status === 'aborted' && <span className="ac-chip-err">broken off before the answer was complete</span>}
          </div>
        )}
      </button>
      <time title={new Date(e.at).toLocaleString('de-CH')}>{compactRow ? ago(e.at) : timeOf(e.at)}</time>
    </div>
  );
}

function groupByDay(events) {
  const groups = new Map();
  events.forEach((e) => {
    const k = dayKey(e.at);
    if (!groups.has(k)) groups.set(k, { k, label: dayLabel(e.at), items: [] });
    groups.get(k).items.push(e);
  });
  return [...groups.values()];
}

function Legend({ hidden, toggle }) {
  return (
    <div className="ac-legend">
      {CATEGORIES.map((c) => (
        <button key={c.key} type="button" className={hidden.includes(c.key) ? 'off' : ''} onClick={() => toggle(c.key)}>
          <i className={`ac-dot ac-bg-${c.key}`} /> {c.label}
        </button>
      ))}
    </div>
  );
}

function useHidden(key) {
  const [hidden, setHidden] = useState(store.get(key, ['access']));
  const toggle = (k) => setHidden((h) => { const n = h.includes(k) ? h.filter((x) => x !== k) : [...h, k]; store.set(key, n); return n; });
  return [hidden, toggle];
}

const isOnline = (iso) => iso && Date.now() - new Date(iso).getTime() < 15 * 60 * 1000;

// ── Overview ─────────────────────────────────────────────────────────────────

function Overview({ data, hours, period, go, onPerson, onThread, onEvent }) {
  const k = data.kpis;
  const p = data.previous;
  const rows = useMemo(() => seriesByBucket(data.series, hours), [data.series, hours]);
  const [hidden, toggle] = useHidden('hidden');
  const col = (cat) => rows.map((r) => r[cat] || 0);
  const people = [...data.people].filter((x) => x.events > 0).sort((a, b) => b.events - a.events);
  const online = data.people.filter((x) => isOnline(x.last_ever));
  const audioMin = Math.round((k.audio_seconds || 0) / 60);
  return (
    <div className="ac-overview">
      {online.length > 0 && (
        <div className="ac-online">
          <i className="ac-live-dot" /> <b>{online.length} {online.length === 1 ? 'person' : 'people'} active right now</b>
          <span className="ac-online-people">
            {online.map((u) => (
              <button key={u.user_id} type="button" onClick={() => onPerson(u.user_id)} title={fullName(u) || u.username}>
                <Avatar user={u} size={26} /> {u.username}
              </button>
            ))}
          </span>
        </div>
      )}

      <div className="ac-kpis">
        <Kpi label="Active people" value={num(k.active_users)} d={delta(k.active_users, p.active_users)}
          sub={`of ${data.people.length} accounts · ${num(k.logins)} logins`} spark={rows.map((r) => r.total)} sparkColor="var(--ac-accent)" />
        <Kpi label="Questions" value={num(k.questions)} d={delta(k.questions, p.questions)}
          sub={`${num(k.conversations)} conversations`} spark={col('chat')} sparkColor="var(--ac-chat)" />
        <Kpi label="Audio" value={num(k.audio_plays)} d={delta(k.audio_plays, p.audio_plays)}
          sub={`${audioMin} min listened`} spark={col('audio')} sparkColor="var(--ac-audio)" />
        <Kpi label="Analyses" value={num(k.analyses)} d={delta(k.analyses, p.analyses)}
          sub="ARE runs, deepenings, dossiers" spark={col('analysis')} sparkColor="var(--ac-analysis)" />
        <Kpi label="Documents" value={num(k.documents)} d={delta(k.documents, p.documents)}
          sub={`created · ${num(k.opened)} opened`} spark={col('document')} sparkColor="var(--ac-document)" />
        <Kpi label="Answer time" value={k.answer_ms_median ? duration(k.answer_ms_median) : '—'}
          badge={k.chat_errors ? `${k.chat_errors} failed` : null}
          sub={k.errors ? `median · ${num(k.errors)} failed ${k.errors === 1 ? 'action' : 'actions'}` : 'median · nothing failed'} />
      </div>

      <div className="ac-grid two">
        <Card title="Activity" icon={faWaveSquare} action={<Legend hidden={hidden} toggle={toggle} />}>
          <StackedBars rows={rows} hourly={hours <= 48} hidden={hidden} />
        </Card>
        <Card title="Latest" icon={faTowerBroadcast} action={<button type="button" className="ac-link" onClick={() => go('feed')}>Live feed <FontAwesomeIcon icon={faChevronRight} /></button>}>
          <div className="ac-latest">
            {data.recent.length ? data.recent.slice(0, 9).map((e) => (
              <EventRow key={e.id} e={e} onEvent={onEvent} onPerson={onPerson} compactRow />
            )) : <div className="ac-empty small">Nothing in the {period.long}.</div>}
          </div>
        </Card>
      </div>

      <div className="ac-grid three">
        <Card title="When people work" icon={faClock}><Heatmap cells={data.heatmap} /></Card>
        <Card title="Assistants" icon={faComments}>
          <BarList items={data.areas.map((a) => ({
            label: areaLabel(a.area), value: a.questions, tone: 'chat',
            sub: `${a.users} ${a.users === 1 ? 'person' : 'people'}${a.answer_ms ? ` · ⌀ ${duration(a.answer_ms)}` : ''}${a.errors ? ` · ${a.errors} failed` : ''}`,
          }))} empty="No questions yet" />
        </Card>
        <Card title="What got created" icon={faFileLines}>
          <BarList items={data.outputs.map((o) => ({ label: docTitle(o.type === 'chart_saved' ? 'chart' : o.type), value: o.n, tone: 'document' }))} empty="No documents yet" />
          {data.tools.length > 0 && (
            <div className="ac-tools-cloud">
              <div className="ac-mini-title">Most used tools</div>
              {data.tools.slice(0, 8).map((t) => <span key={t.tool} className="ac-tool">{t.tool} <b>{t.n}</b></span>)}
            </div>
          )}
        </Card>
      </div>

      <div className="ac-grid two">
        <Card title="Most active" icon={faUsers} action={<button type="button" className="ac-link" onClick={() => go('people')}>All people <FontAwesomeIcon icon={faChevronRight} /></button>}>
          <div className="ac-leader">
            {people.slice(0, 7).map((u, i) => (
              <button type="button" key={u.user_id} className="ac-leader-row" onClick={() => onPerson(u.user_id)}>
                <span className="ac-rank">{i + 1}</span>
                <Avatar user={u} size={34} />
                <span className="ac-leader-name"><b>{u.username}</b><em>{fullName(u) || u.role}</em></span>
                <DayStrip days={u.days} count={hours <= 168 ? 7 : 14} />
                <span className="ac-leader-figs">
                  <span title="Questions"><FontAwesomeIcon icon={faComments} /> {compact(u.questions)}</span>
                  <span title="Audio"><FontAwesomeIcon icon={faHeadphones} /> {compact(u.audio)}</span>
                  <span title="Analyses"><FontAwesomeIcon icon={faMagnifyingGlassChart} /> {compact(u.analyses)}</span>
                </span>
              </button>
            ))}
            {!people.length && <div className="ac-empty small">Nobody was active in the {period.long}.</div>}
          </div>
        </Card>
        <Card title="Needs attention" icon={faTriangleExclamation} className={data.errors.length ? 'attention' : ''}>
          {data.errors.length ? (
            <div className="ac-latest">
              {data.errors.map((e) => <EventRow key={e.id} e={e} onEvent={onEvent} onPerson={onPerson} compactRow />)}
            </div>
          ) : (
            <div className="ac-calm"><span>✓</span> Nothing failed in the {period.long}.</div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── People ───────────────────────────────────────────────────────────────────

const SORTS = {
  last: (a, b) => (b.last_ever || '').localeCompare(a.last_ever || ''),
  questions: (a, b) => b.questions - a.questions,
  audio: (a, b) => b.audio - a.audio,
  analyses: (a, b) => b.analyses - a.analyses,
  documents: (a, b) => b.documents - a.documents,
  errors: (a, b) => b.errors - a.errors,
  name: (a, b) => a.username.localeCompare(b.username),
};

function People({ people, onPerson, hours }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(store.get('people-sort', 'last'));
  const [showQuiet, setShowQuiet] = useState(true);
  const setS = (s) => { setSort(s); store.set('people-sort', s); };
  const list = people
    .filter((u) => !q || `${u.username} ${fullName(u)} ${u.role}`.toLowerCase().includes(q.toLowerCase()))
    .filter((u) => showQuiet || u.events > 0)
    .sort(SORTS[sort] || SORTS.last);
  const H = ({ k, children, num: numeric }) => (
    <th className={`${numeric ? 'num' : ''}${sort === k ? ' on' : ''}`}><button type="button" onClick={() => setS(k)}>{children}</button></th>
  );
  return (
    <div className="ac-page">
      <div className="ac-toolbar">
        <label className="ac-search"><FontAwesomeIcon icon={faMagnifyingGlass} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" /></label>
        <label className="ac-toggle"><input type="checkbox" checked={showQuiet} onChange={(e) => setShowQuiet(e.target.checked)} /> Show people without activity</label>
        <span className="ac-muted">{list.length} of {people.length}</span>
      </div>
      <div className="ac-table-wrap">
        <table className="ac-table">
          <thead>
            <tr>
              <H k="name">Person</H><H k="last">Last active</H><th>{hours <= 168 ? 'Last 7 days' : 'Last 14 days'}</th>
              <H k="questions" num>Questions</H><H k="audio" num>Audio</H><H k="analyses" num>Analyses</H>
              <H k="documents" num>Docs</H><H k="errors" num>Errors</H><th>Uses</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.user_id} onClick={() => onPerson(u.user_id)} className={u.status === 'inactive' ? 'dim' : ''}>
                <td>
                  <div className="ac-person-cell">
                    <span className="ac-avatar-wrap"><Avatar user={u} size={36} />{isOnline(u.last_ever) && <i className="ac-online-dot" />}</span>
                    <span><b>{u.username}</b><em>{fullName(u) || '—'} · {u.role}{u.status === 'inactive' ? ' · inactive' : ''}</em></span>
                  </div>
                </td>
                <td>
                  <span className={isOnline(u.last_ever) ? 'ac-text-ok' : ''}>{isOnline(u.last_ever) ? 'active now' : ago(u.last_ever)}</span>
                  {u.home_instance && <div className="ac-tag">works on {u.home_instance}</div>}
                </td>
                <td><DayStrip days={u.days} count={hours <= 168 ? 7 : 14} /></td>
                <td className="num">{u.questions ? <b>{num(u.questions)}</b> : <span className="ac-zero">0</span>}{u.conversations > 0 && <em className="ac-sub-num">{u.conversations} conv.</em>}</td>
                <td className="num">{u.audio ? num(u.audio) : <span className="ac-zero">0</span>}</td>
                <td className="num">{u.analyses ? num(u.analyses) : <span className="ac-zero">0</span>}</td>
                <td className="num">{u.documents ? num(u.documents) : <span className="ac-zero">0</span>}</td>
                <td className="num">{u.errors ? <span className="ac-text-error">{num(u.errors)}</span> : <span className="ac-zero">0</span>}</td>
                <td><div className="ac-uses">{(u.areas || []).map((a) => <span key={a} className="ac-pill">{areaLabel(a)}</span>)}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div className="ac-empty">No one matches.</div>}
      </div>
    </div>
  );
}

// ── Person ───────────────────────────────────────────────────────────────────

function Person({ id, client, hours, period, back, rev, onThread, onEvent, onPerson }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [hidden, toggle] = useHidden('person-hidden');
  useEffect(() => {
    let off = false;
    setError('');
    client.get(`/api/admin/activity/people/${id}?hours=${hours}`)
      .then((v) => { if (!off) setData(v); })
      .catch((e) => { if (!off) setError(e.message); });
    return () => { off = true; };
  }, [client, id, hours, rev]);
  const rows = useMemo(() => (data ? seriesByBucket(data.series, hours) : []), [data, hours]);
  if (error) return <div className="ac-alert big">{error}</div>;
  if (!data || data.user.user_id !== id) return <div className="ac-empty tall"><span className="ac-spinner" /></div>;
  const u = data.user;
  const k = data.kpis;
  const p = data.previous;
  const groups = groupByDay(data.events);
  return (
    <div className="ac-page">
      <button type="button" className="ac-back" onClick={back}><FontAwesomeIcon icon={faArrowLeft} /> People</button>
      <section className="ac-hero">
        <Avatar user={u} size={68} />
        <div className="ac-hero-text">
          <h2>{fullName(u) || u.username}</h2>
          <div className="ac-hero-sub">
            <span>{u.username}</span><span>{u.role}</span>
            <span className={u.status === 'inactive' ? 'ac-text-error' : 'ac-text-ok'}>{u.status}</span>
            {u.email && <span>{u.email}</span>}
          </div>
          <div className="ac-hero-chips">
            {Object.entries(data.assistants || {}).map(([a, note]) => (
              <span key={a} className="ac-pill strong">{areaLabel(a)}{note.data_set ? ` · ${note.data_set}` : ' · no access'}</span>
            ))}
            {u.home_instance && <span className="ac-pill">works on {u.home_instance}</span>}
          </div>
        </div>
        <dl className="ac-hero-facts">
          <div><dt>First seen</dt><dd>{data.first_seen ? ago(data.first_seen) : '—'}</dd></div>
          <div><dt>Last login</dt><dd>{ago(u.last_login)}</dd></div>
          <div><dt>Last active</dt><dd>{data.events[0] ? ago(data.events[0].at) : '—'}</dd></div>
        </dl>
      </section>

      <div className="ac-kpis small">
        <Kpi label="Questions" value={num(k.questions)} d={delta(k.questions, p.questions)} sub={`${num(k.conversations)} conversations`} />
        <Kpi label="Audio" value={num(k.audio_plays)} d={delta(k.audio_plays, p.audio_plays)} sub={`${Math.round((k.audio_seconds || 0) / 60)} min listened`} />
        <Kpi label="Analyses" value={num(k.analyses)} d={delta(k.analyses, p.analyses)} sub="runs & dossiers" />
        <Kpi label="Documents" value={num(k.documents)} d={delta(k.documents, p.documents)} sub={`created · ${num(k.opened)} opened`} />
        <Kpi label="Answer time" value={k.answer_ms_median ? duration(k.answer_ms_median) : '—'}
          badge={k.chat_errors ? `${k.chat_errors} failed` : null}
          sub={k.errors ? `median · ${k.errors} failed` : 'median · nothing failed'} />
      </div>

      <div className="ac-grid two">
        <Card title="Activity" icon={faWaveSquare} action={<Legend hidden={hidden} toggle={toggle} />}>
          <StackedBars rows={rows} hourly={hours <= 48} hidden={hidden} height={200} />
        </Card>
        <Card title="When" icon={faClock}><Heatmap cells={data.heatmap} /></Card>
      </div>

      <div className="ac-grid person">
        <Card title="Timeline" icon={faTowerBroadcast}>
          {groups.length ? groups.map((g) => (
            <div key={g.k} className="ac-day">
              <div className="ac-day-label">{g.label}</div>
              {g.items.map((e) => <EventRow key={e.id} e={e} onEvent={onEvent} onPerson={onPerson} showUser={false} />)}
            </div>
          )) : <div className="ac-empty small">Nothing in the {period.long}.</div>}
        </Card>
        <Card title="Conversations" icon={faComments}>
          <ThreadList threads={data.threads} onOpen={onThread} />
          {data.areas.length > 0 && (
            <div className="ac-person-areas">
              <div className="ac-mini-title">Assistants</div>
              <BarList items={data.areas.map((a) => ({ label: areaLabel(a.area), value: a.questions, tone: 'chat', sub: a.answer_ms ? `⌀ ${duration(a.answer_ms)}` : '' }))} />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ThreadList({ threads, onOpen, selected, showUser }) {
  if (!threads.length) return <div className="ac-empty small">No conversations.</div>;
  return (
    <div className="ac-threads">
      {threads.map((t) => (
        <button type="button" key={t.thread} className={`ac-thread${selected === t.thread ? ' on' : ''}${t.errors ? ' error' : ''}`} onClick={() => onOpen(t.thread)}>
          <div className="ac-thread-top">
            {showUser && <Avatar user={t} size={22} />}
            {showUser && <b>{t.username}</b>}
            <span className="ac-pill">{areaLabel(t.area)}</span>
            {t.voice && <FontAwesomeIcon icon={faMicrophone} className="ac-muted" />}
            <time>{ago(t.last_at)}</time>
          </div>
          <div className="ac-thread-title">{t.title || '—'}</div>
          {t.turns > 1 && t.last_title && t.last_title !== t.title && <div className="ac-thread-last">… {t.last_title}</div>}
          <div className="ac-thread-meta">
            <span>{t.turns} {t.turns === 1 ? 'question' : 'questions'}</span>
            <span><FontAwesomeIcon icon={faClock} /> {duration(t.duration_ms)}</span>
            {t.data_set && <span><FontAwesomeIcon icon={faDatabase} /> {t.data_set}</span>}
            {t.errors > 0 && <span className="ac-text-error"><FontAwesomeIcon icon={faTriangleExclamation} /> {t.errors}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

// ── Conversations: inbox + reading pane ──────────────────────────────────────

function Conversations({ client, hours, people, rev, onPerson }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [area, setArea] = useState('');
  const [userId, setUserId] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [threads, setThreads] = useState(null);
  const [more, setMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const params = useCallback((before) => {
    const s = new URLSearchParams({ hours, limit: 50 });
    if (query) s.set('q', query);
    if (area) s.set('area', area);
    if (userId) s.set('user_id', userId);
    if (before) s.set('before_at', before);
    return s.toString();
  }, [hours, query, area, userId]);
  useEffect(() => {
    let off = false;
    setThreads(null);
    client.get(`/api/admin/activity/threads?${params()}`)
      .then((v) => { if (off) return; setThreads(v); setMore(v.length === 50); setSelected((s) => s && v.some((t) => t.thread === s) ? s : v[0]?.thread || null); })
      .catch((e) => { if (!off) setError(e.message); });
    return () => { off = true; };
  }, [client, params, rev]);
  const loadMore = async () => {
    const last = threads[threads.length - 1];
    const v = await client.get(`/api/admin/activity/threads?${params(last.last_at.replace('Z', ''))}`);
    setThreads((t) => [...t, ...v]);
    setMore(v.length === 50);
  };
  const list = (threads || []).filter((t) => !onlyErrors || t.errors > 0);
  const areas = [...new Set(people.flatMap((u) => u.areas || []))];
  return (
    <div className="ac-inbox">
      <div className="ac-inbox-list">
        <div className="ac-toolbar column">
          <label className="ac-search"><FontAwesomeIcon icon={faMagnifyingGlass} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions and answers" /></label>
          <div className="ac-filters">
            <select value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Everyone</option>
              {people.filter((u) => u.conversations > 0).map((u) => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
            </select>
            <select value={area} onChange={(e) => setArea(e.target.value)}>
              <option value="">All assistants</option>
              {areas.map((a) => <option key={a} value={a}>{areaLabel(a)}</option>)}
            </select>
            <label className="ac-toggle"><input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} /> Failed only</label>
          </div>
        </div>
        <div className="ac-inbox-scroll">
          {error && <div className="ac-alert">{error}</div>}
          {!threads ? <div className="ac-empty"><span className="ac-spinner" /></div>
            : <ThreadList threads={list} onOpen={setSelected} selected={selected} showUser />}
          {more && !onlyErrors && <button type="button" className="ac-btn ghost wide" onClick={loadMore}>Load older</button>}
        </div>
      </div>
      <div className="ac-inbox-read">
        {selected ? <Transcript client={client} threadKey={selected} onPerson={onPerson} />
          : <div className="ac-empty tall">{threads ? 'No conversations in this period.' : ''}</div>}
      </div>
    </div>
  );
}

// ── Live feed ────────────────────────────────────────────────────────────────

function Feed({ client, hours, people, rev, onEvent, onPerson }) {
  const [cat, setCat] = useState('');
  const [userId, setUserId] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [events, setEvents] = useState(null);
  const [more, setMore] = useState(false);
  const fresh = useRef(new Set());
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const params = useCallback((before) => {
    const s = new URLSearchParams({ hours, limit: 60 });
    if (cat) s.set('category', cat);
    if (userId) s.set('user_id', userId);
    if (onlyErrors) s.set('status', 'error');
    if (query) s.set('q', query);
    if (before) s.set('before', before);
    return s.toString();
  }, [hours, cat, userId, onlyErrors, query]);
  const load = useCallback(async () => {
    const v = await client.get(`/api/admin/activity/events?${params()}`);
    setEvents((old) => {
      if (old) {
        const known = new Set(old.map((e) => e.id));
        v.forEach((e) => { if (!known.has(e.id)) fresh.current.add(e.id); });
      }
      return v;
    });
    setMore(v.length === 60);
  }, [client, params]);
  useEffect(() => { setEvents(null); fresh.current = new Set(); load().catch(() => setEvents([])); }, [load, rev]);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load().catch(() => {}); }, 15000);
    return () => clearInterval(id);
  }, [load]);
  const loadMore = async () => {
    const v = await client.get(`/api/admin/activity/events?${params(events[events.length - 1].id)}`);
    setEvents((e) => [...e, ...v]);
    setMore(v.length === 60);
  };
  const groups = groupByDay(events || []);
  return (
    <div className="ac-page">
      <div className="ac-toolbar">
        <label className="ac-search"><FontAwesomeIcon icon={faMagnifyingGlass} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search everything" /></label>
        <div className="ac-catbar">
          <button type="button" className={!cat ? 'on' : ''} onClick={() => setCat('')}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c.key} type="button" className={cat === c.key ? 'on' : ''} onClick={() => setCat(cat === c.key ? '' : c.key)}>
              <i className={`ac-dot ac-bg-${c.key}`} /> {c.label}
            </button>
          ))}
        </div>
        <select className="ac-select-person" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Everyone</option>
          {people.filter((u) => u.events > 0).map((u) => <option key={u.user_id} value={u.user_id}>{u.username}</option>)}
        </select>
        <label className="ac-toggle"><input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} /> Failed only</label>
        <span className="ac-live"><i className="ac-live-dot" /> live</span>
      </div>
      {!events ? <div className="ac-empty tall"><span className="ac-spinner" /></div> : (
        <div className="ac-feed">
          {groups.map((g) => (
            <div key={g.k} className="ac-day">
              <div className="ac-day-label">{g.label}<span>{g.items.length}</span></div>
              {g.items.map((e) => (
                <div key={e.id} className={fresh.current.has(e.id) ? 'ac-fresh' : ''}>
                  <EventRow e={e} onEvent={onEvent} onPerson={onPerson} />
                </div>
              ))}
            </div>
          ))}
          {!events.length && <div className="ac-empty">Nothing matches.</div>}
          {more && <button type="button" className="ac-btn ghost wide" onClick={loadMore}>Load older</button>}
        </div>
      )}
    </div>
  );
}
